import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiUsers, FiPlus, FiSearch, FiEdit2, FiDownload } from '../utils/icons';
import { getAllEmployees, getOrganizations } from '../services/adminService';
import { Employee, Organization } from '../types';
import { EditEmployeeModal } from '../components/employee/EditEmployeeModal';
import { validateEmployeeForAppointmentLetter, generateAppointmentLetter } from '../utils/appointmentLetterGenerator';

export const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0, terminated: 0 });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showSalaryTypeDialog, setShowSalaryTypeDialog] = useState(false);
  const [appointmentLetterEmployee, setAppointmentLetterEmployee] = useState<Employee | null>(null);
  const [appointmentLetterDate, setAppointmentLetterDate] = useState<string>(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadOrganizations();
    loadEmployees();
  }, [currentPage]);

  const loadOrganizations = async () => {
    try {
      const response = await getOrganizations(undefined, { page: 1, limit: 100 });
      if (response.success && response.data) {
        setOrganizations(response.data);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllEmployees(undefined, { page: currentPage, limit: 100 });
      if (response.success && response.data) {
        setEmployees(response.data);
        setTotalPages(response.pagination?.totalPages || 1);

        // Update total employees and status counts from summary if available
        if (response.summary) {
          setTotalEmployees(response.summary.totalEmployees || response.pagination?.total || response.data.length);
          setStatusCounts({
            active: response.summary.activeEmployees || 0,
            inactive: response.summary.inactiveEmployees || 0,
            terminated: response.summary.terminatedEmployees || 0
          });
        } else {
          // Fallback to pagination total if no summary
          setTotalEmployees(response.pagination?.total || response.data.length);
        }
      } else {
        setError('Failed to load employees');
      }
    } catch (err) {
      setError('An error occurred while loading employees');
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const getOrgName = (orgId: number | undefined): string => {
    if (!orgId) return 'Unknown';
    const org = organizations.find((o) => o.orgId === orgId);
    return org?.name || `Org #${orgId}`;
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      !searchQuery ||
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesOrg = !selectedOrgId || employee.orgId === parseInt(selectedOrgId);
    const matchesStatus = !statusFilter || employee.status === statusFilter;

    return matchesSearch && matchesOrg && matchesStatus;
  });

  const stats = {
    total: totalEmployees,
    active: statusCounts.active,
    inactive: statusCounts.inactive,
    terminated: statusCounts.terminated,
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    loadEmployees();
    setSelectedEmployee(null);
  };

  const handleDownloadAppointmentLetter = (employee: Employee) => {
    // Validate employee data
    const validation = validateEmployeeForAppointmentLetter(employee);

    if (!validation.isValid) {
      alert(`Cannot generate appointment letter. Missing required fields:\n${validation.missingFields.join('\n')}`);
      return;
    }

    // Show dialog to choose salary type
    setAppointmentLetterEmployee(employee);
    setShowSalaryTypeDialog(true);
  };

  const generateAppointmentLetterWithType = (salaryType: 'ctc' | 'inhand') => {
    if (!appointmentLetterEmployee) return;

    try {
      // Get organization name
      const organizationName = appointmentLetterEmployee.orgName || getOrgName(appointmentLetterEmployee.orgId);

      // Get office name from location names if available
      const officeName = appointmentLetterEmployee.locationNames && appointmentLetterEmployee.locationNames.length > 0
        ? appointmentLetterEmployee.locationNames.map(loc => loc.name).filter(Boolean).join(', ')
        : undefined;

      // Parse the selected date
      const letterDate = appointmentLetterDate ? new Date(appointmentLetterDate) : new Date();

      // Generate and download the appointment letter
      generateAppointmentLetter({
        employee: appointmentLetterEmployee,
        organizationName,
        officeName,
        salaryType,
        letterDate
      });

      // Close dialog
      setShowSalaryTypeDialog(false);
      setAppointmentLetterEmployee(null);
    } catch (error) {
      console.error('Error generating appointment letter:', error);
      alert('An error occurred while generating the appointment letter. Please try again.');
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>
            <FiUsers />
            Employees
          </Title>
          <Subtitle>Manage and monitor all employees</Subtitle>
        </HeaderLeft>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total Employees</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#10b981' }}>{stats.active}</StatValue>
          <StatLabel>Active</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#f59e0b' }}>{stats.inactive}</StatValue>
          <StatLabel>Inactive</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#ef4444' }}>{stats.terminated}</StatValue>
          <StatLabel>Terminated</StatLabel>
        </StatCard>
      </StatsContainer>

      <FiltersContainer>
        <SearchBar>
          <FiSearch />
          <SearchInput
            type="text"
            placeholder="Search by name, employee ID, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        <FilterGroup>
          <Select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}>
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.orgId} value={org.orgId}>
                {org.name}
              </option>
            ))}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="terminated">Terminated</option>
          </Select>
        </FilterGroup>
      </FiltersContainer>

      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading employees...</LoadingText>
        </LoadingContainer>
      ) : error ? (
        <EmptyState>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyTitle>Error</EmptyTitle>
          <EmptyText>{error}</EmptyText>
        </EmptyState>
      ) : filteredEmployees.length === 0 ? (
        <EmptyState>
          <EmptyIcon>👥</EmptyIcon>
          <EmptyTitle>No employees found</EmptyTitle>
          <EmptyText>
            {searchQuery || selectedOrgId || statusFilter
              ? 'Try adjusting your filters'
              : 'Get started by adding your first employee'}
          </EmptyText>
        </EmptyState>
      ) : (
        <EmployeesGrid>
          {filteredEmployees.map((employee) => (
            <EmployeeCard key={employee.id}>
              <EmployeeHeader>
                <EmployeeIcon>
                  <FiUsers />
                </EmployeeIcon>
                <EmployeeInfo>
                  <EmployeeName>{employee.name}</EmployeeName>
                  <EmployeeOrg>{getOrgName(employee.orgId)}</EmployeeOrg>
                </EmployeeInfo>
                <HeaderActions>
                  <StatusBadge $status={employee.status}>
                    {employee.status}
                  </StatusBadge>
                  <ActionButton onClick={() => handleEditEmployee(employee)} title="Edit employee">
                    <FiEdit2 />
                  </ActionButton>
                </HeaderActions>
              </EmployeeHeader>

              <EmployeeBody>
                <EmployeeDetail>
                  <DetailLabel>Employee ID</DetailLabel>
                  <DetailValue>{employee.employeeId || '-'}</DetailValue>
                </EmployeeDetail>

                <EmployeeDetail>
                  <DetailLabel>Designation</DetailLabel>
                  <DetailValue>{employee.designation || '-'}</DetailValue>
                </EmployeeDetail>

                <EmployeeDetail>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue>{employee.email || '-'}</DetailValue>
                </EmployeeDetail>

                <EmployeeDetail>
                  <DetailLabel>Mobile</DetailLabel>
                  <DetailValue>{employee.mobileNumber || '-'}</DetailValue>
                </EmployeeDetail>

                <EmployeeDetail>
                  <DetailLabel>Employment Type</DetailLabel>
                  <DetailValue>{employee.employmentType || '-'}</DetailValue>
                </EmployeeDetail>

                {employee.salaryComponents && employee.salaryComponents.length > 0 && (
                  <SalarySection>
                    <DetailLabel>Salary Components</DetailLabel>
                    <SalaryList>
                      {employee.salaryComponents.map((comp, idx) => (
                        <SalaryItem key={idx}>
                          <span>{comp.component}:</span>
                          <span>₹{comp.amount.toLocaleString()}</span>
                        </SalaryItem>
                      ))}
                    </SalaryList>
                    <TotalSalary>
                      Total: ₹{employee.salaryComponents.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                    </TotalSalary>
                  </SalarySection>
                )}
              </EmployeeBody>

              <CardFooter>
                <AppointmentLetterButton onClick={() => handleDownloadAppointmentLetter(employee)} title="Download appointment letter">
                  <FiDownload />
                  <span>Appointment Letter</span>
                </AppointmentLetterButton>
              </CardFooter>
            </EmployeeCard>
          ))}
        </EmployeesGrid>
      )}

      {!loading && !error && totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Page {currentPage} of {totalPages} ({totalEmployees} total employees)
          </PaginationInfo>
          <PaginationControls>
            <PaginationButton onClick={handlePreviousPage} disabled={currentPage === 1}>
              Previous
            </PaginationButton>
            <PageNumber>{currentPage}</PageNumber>
            <PaginationButton onClick={handleNextPage} disabled={currentPage === totalPages}>
              Next
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}

      <EditEmployeeModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        employee={selectedEmployee}
      />

      {/* Salary Type Selection Dialog */}
      {showSalaryTypeDialog && (
        <DialogOverlay onClick={() => setShowSalaryTypeDialog(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>Select Appointment Letter Type</DialogTitle>
            <DialogContent>
              <DatePickerSection>
                <DatePickerLabel>Letter Date</DatePickerLabel>
                <DatePickerInput
                  type="date"
                  value={appointmentLetterDate}
                  onChange={(e) => setAppointmentLetterDate(e.target.value)}
                />
              </DatePickerSection>
              <DialogButton onClick={() => generateAppointmentLetterWithType('ctc')}>
                <ButtonText>
                  <ButtonLabel>CTC Version</ButtonLabel>
                  <ButtonDescription>Shows Cost to Company with all components</ButtonDescription>
                </ButtonText>
              </DialogButton>
              <DialogButton onClick={() => generateAppointmentLetterWithType('inhand')}>
                <ButtonText>
                  <ButtonLabel>In-Hand Version</ButtonLabel>
                  <ButtonDescription>Shows Earnings - Deductions = Net In-Hand</ButtonDescription>
                </ButtonText>
              </DialogButton>
            </DialogContent>
            <DialogCancel onClick={() => setShowSalaryTypeDialog(false)}>Cancel</DialogCancel>
          </DialogBox>
        </DialogOverlay>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    font-size: 32px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FiltersContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    color: ${({ theme }) => theme.colors.text.hint};
    font-size: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.hint};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Select = styled.select`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.divider};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const EmptyState = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin: 0;
`;

const EmployeesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const EmployeeCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const EmployeeHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const EmployeeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EmployeeInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EmployeeName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmployeeOrg = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    font-size: 14px;
  }
`;

const AppointmentLetterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  svg {
    font-size: 14px;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${({ $status }) =>
    $status === 'active' ? '#10b98120' :
    $status === 'inactive' ? '#f59e0b20' :
    '#ef444420'};
  color: ${({ $status }) =>
    $status === 'active' ? '#10b981' :
    $status === 'inactive' ? '#f59e0b' :
    '#ef4444'};
  flex-shrink: 0;
`;

const EmployeeBody = styled.div`
  padding: 20px;
`;

const CardFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: flex-end;
  background: ${({ theme }) => theme.colors.background};
`;

const EmployeeDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
  word-break: break-word;
`;

const SalarySection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const SalaryList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SalaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  span:first-child {
    font-weight: 500;
  }

  span:last-child {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const TotalSalary = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-align: right;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumber = styled.div`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`;

// Dialog styled components
const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DialogBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const DialogTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DialogButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border: 2px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }
`;

const DatePickerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const DatePickerLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DatePickerInput = styled.input`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ButtonLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ButtonDescription = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DialogCancel = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 12px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
`;
